import { useCallback, useState } from "react";
import { CheckCircle2, Loader2, Lock, Save, XCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { InvoiceRecordHeader } from "@/components/invoices/detail/InvoiceDetailChrome";
import { InvoiceDetailForm } from "@/components/invoices/detail/InvoiceDetailForm";
import {
  InvoiceDetailActivityPanel,
  InvoiceDetailAlerts,
  InvoiceDetailAssignment,
  InvoiceDetailCommentsPanel,
} from "@/components/invoices/detail/InvoiceDetailAside";
import { cn } from "@/lib/utils";

const BASE_TABS = [
  { id: "details", label: "Details" },
  { id: "comments", label: "Comments", countKey: "comments" },
  { id: "activity", label: "Activity", countKey: "activity" },
];

/**
 * When the page is too narrow to show the document beside the form, the document
 * becomes the first tab rather than a pane stacked above a page scroll.
 *
 * Stacking put a pinch-and-pan PDF surface inside a scrolling page — two gesture
 * handlers competing for the same drag — and pushed the commit bar past the
 * whole form, so Save and Approve were only reachable by scrolling to the end.
 * As a tab each surface owns the viewport while it is in use, and the commit bar
 * stays pinned exactly as it is on desktop.
 */
const DOCUMENT_TAB = { id: "document", label: "Document" };

/**
 * The work column: who this invoice is (header), what to look at (tabs), the
 * work itself (scrolling body), and how to commit it (pinned bar).
 *
 * The commit actions used to sit at the bottom of the Details tab's scroll, so
 * Save and Approve could be scrolled out of reach and were unavailable at all
 * while reading Comments. They are pinned to the panel now, the same way the
 * queue table pins its footer.
 */
export function InvoiceDetailRightPanel({
  detail,
  form,
  originalForm,
  fieldErrors,
  viewOnly,
  isSaving,
  showComplete,
  showApprove,
  showReject,
  lockReason,
  onFormChange,
  approvalError,
  approvalAttempt,
  onBack,
  // record header
  onToggleStar,
  isTogglingStar,
  onExtract,
  isExtracting,
  canExtract,
  canEdit,
  isViewRoute,
  onChangeCategory,
  onChangeStatus,
  // assignment
  assignees,
  onRequestAssignees,
  onAssigneeChange,
  clientName,
  clientNameError,
  onClientNameChange,
  onClientNameSave,
  isSavingClientName,
  // comments
  comments,
  isLoadingComments,
  isSubmittingComment,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  // activity
  activity,
  isLoadingActivity,
  // actions
  onSave,
  onComplete,
  onApprove,
  onRejectOpen,
  // The document, when this panel is the only panel.
  documentSlot = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = documentSlot ? [DOCUMENT_TAB, ...BASE_TABS] : BASE_TABS;

  // Arriving from the queue's comment icon opens straight on Comments; that
  // link is what the reader clicked, so landing on Details would lose the intent.
  // Otherwise the single-panel layout opens on the document, which is what the
  // stacked layout put at the top of the page and what the operator came to read.
  const requestedTab = tabs.some((tab) => tab.id === location.state?.tab)
    ? location.state.tab
    : documentSlot
      ? "document"
      : "details";
  const [activeTab, setActiveTab] = useState(requestedTab);

  // A save blocked by the approval check has to show its error, and the control
  // lives on the Details tab, so bring that tab forward. Done during render,
  // keyed on the attempt count, rather than in an effect.
  const [shownAttempt, setShownAttempt] = useState(approvalAttempt);
  if (approvalAttempt !== shownAttempt) {
    setShownAttempt(approvalAttempt);
    setActiveTab("details");
  }

  // Rotating a tablet into landscape can split the page and take the Document
  // tab away underneath a reader who is standing on it. Resolved during render
  // rather than in an effect, so the panel never paints empty for a frame.
  const resolvedTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : "details";
  const hasFieldErrors = Object.keys(fieldErrors ?? {}).length > 0;

  const counts = { comments: comments?.length ?? 0, activity: activity?.length ?? 0 };
  const getTabCount = (countKey) => (countKey ? (counts[countKey] ?? 0) : null);

  const focusTab = useCallback((tabId) => {
    document.getElementById(`invoice-tab-${tabId}`)?.focus();
  }, []);

  const handleTabKeyDown = (event, tabIndex) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") {
      nextIndex = (tabIndex + 1) % tabs.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (tabIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabs.length - 1;
    }

    if (nextIndex == null) return;

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab.id);
    focusTab(nextTab.id);
  };

  const showCommitBar = !viewOnly;

  return (
    <div className="page-panel-wrap invoice-detail-right-wrap">
      <section className="invoice-detail-right page-panel">
        <InvoiceRecordHeader
          detail={detail}
          viewOnly={isViewRoute}
          onBack={onBack}
          onToggleStar={onToggleStar}
          isTogglingStar={isTogglingStar}
          onExtract={onExtract}
          isExtracting={isExtracting}
          canExtract={canExtract}
          canEdit={canEdit}
          onChangeCategory={onChangeCategory}
          onChangeStatus={onChangeStatus}
        />

        <div className="invoice-detail-tabs page-toolbar">
          <div
            className="queue-chip-bar invoice-detail-tabs-bar"
            role="tablist"
            aria-label="Invoice sections"
          >
            {tabs.map((tab, tabIndex) => {
              const count = getTabCount(tab.countKey);
              const isActive = resolvedTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`invoice-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`invoice-panel-${tab.id}`}
                  tabIndex={isActive ? 0 : -1}
                  className={cn(
                    "touch-target-chip inline-flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[0.8rem] border px-3 text-sm font-medium transition-colors",
                    isActive
                      ? "queue-chip-active"
                      : "border-transparent bg-transparent text-muted-foreground hover:bg-card/80 hover:text-foreground",
                  )}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(event) => handleTabKeyDown(event, tabIndex)}
                >
                  <span>{tab.label}</span>
                  {tab.countKey != null && (
                    <span className="invoice-detail-tab-count num">{count}</span>
                  )}
                  {tab.id === "details" && hasFieldErrors && (
                    <span className="invoice-detail-tab-alert" aria-label="Has validation errors" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="invoice-detail-tab-panel">
          {/* Stays mounted while another tab is showing. `display: none` would
              hand Syncfusion a zero-size box and it re-measures to nothing on
              reveal; unmounting would re-fetch and re-encode the PDF on every
              tab switch. Held at full size and made invisible instead. */}
          {documentSlot && (
            <div
              role="tabpanel"
              id="invoice-panel-document"
              aria-labelledby="invoice-tab-document"
              className="invoice-detail-tab-pane invoice-detail-doc-pane"
              data-active={resolvedTab === "document" ? "true" : "false"}
              inert={resolvedTab !== "document"}
            >
              {documentSlot}
            </div>
          )}

          {resolvedTab === "details" && (
            <div
              role="tabpanel"
              id="invoice-panel-details"
              aria-labelledby="invoice-tab-details"
              className="invoice-detail-tab-pane"
            >
              <div className="invoice-detail-scroll invoice-detail-tab-scroll">
                <InvoiceDetailAlerts detail={detail} />

                <InvoiceDetailForm
                  form={form}
                  original={originalForm}
                  fieldErrors={fieldErrors}
                  viewOnly={viewOnly}
                  onChange={onFormChange}
                />

                <InvoiceDetailAssignment
                  detail={detail}
                  viewOnly={viewOnly}
                  assignees={assignees}
                  onRequestAssignees={onRequestAssignees}
                  onAssigneeChange={onAssigneeChange}
                  clientName={clientName}
                  clientNameError={clientNameError}
                  onClientNameChange={onClientNameChange}
                  onClientNameSave={onClientNameSave}
                  isSavingClientName={isSavingClientName}
                  isApproved={form.isApproved}
                  approvalError={approvalError}
                  approvalAttempt={approvalAttempt}
                  onApprovedChange={(value) => onFormChange("isApproved", value)}
                />
              </div>
            </div>
          )}

          {resolvedTab === "comments" && (
            <div
              role="tabpanel"
              id="invoice-panel-comments"
              aria-labelledby="invoice-tab-comments"
              className="invoice-detail-tab-pane"
            >
              <div className="invoice-detail-scroll invoice-detail-tab-scroll">
                <InvoiceDetailCommentsPanel
                  comments={comments}
                  isLoading={isLoadingComments}
                  isSubmitting={isSubmittingComment}
                  onAddComment={onAddComment}
                  onUpdateComment={onUpdateComment}
                  onDeleteComment={onDeleteComment}
                />
              </div>
            </div>
          )}

          {resolvedTab === "activity" && (
            <div
              role="tabpanel"
              id="invoice-panel-activity"
              aria-labelledby="invoice-tab-activity"
              className="invoice-detail-tab-pane"
            >
              <div className="invoice-detail-scroll invoice-detail-tab-scroll">
                <InvoiceDetailActivityPanel activity={activity} isLoading={isLoadingActivity} />
              </div>
            </div>
          )}
        </div>

        {showCommitBar ? (
          <div className="invoice-detail-commit">
            <div className="invoice-detail-commit-group">
              {/* All four buttons carry a surface now. Two transparent controls
                  on the left against two coloured ones on the right read as a
                  toolbar with a gap in it rather than a row of four choices. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="button-quiet rounded-full shadow-none"
                onClick={onBack ?? (() => navigate(-1))}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="invoice-commit-save rounded-full shadow-none"
                disabled={isSaving}
                onClick={onSave}
              >
                <Save data-icon="inline-start" />
                Save
              </Button>
            </div>

            <div className="invoice-detail-commit-group">
              {/* Rejecting is destructive and terminal, so it carries the danger
                  token rather than a grey outline with red text. Disabled while
                  a save is in flight — the old app let you open the reject
                  dialog on top of a request that was still running. */}
              {showReject && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="invoice-commit-reject rounded-full shadow-none"
                  disabled={isSaving}
                  onClick={onRejectOpen}
                >
                  <XCircle data-icon="inline-start" />
                  Reject
                </Button>
              )}
              {/* The one action that finishes the job, so it is the only solid
                  button here, in success green — the colour the old app used
                  for it. Brand purple would have made it look like any other
                  primary button in the app. */}
              {showComplete && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="invoice-commit-approve rounded-full"
                  disabled={isSaving}
                  onClick={showApprove ? onApprove : onComplete}
                >
                  {isSaving ? (
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                  ) : (
                    <CheckCircle2 data-icon="inline-start" />
                  )}
                  {showApprove ? "Save & approve" : "Save & complete"}
                </Button>
              )}
            </div>
          </div>
        ) : (
          lockReason && (
            <div className="invoice-detail-locked">
              <Lock className="size-3.5 shrink-0" aria-hidden="true" />
              <p>{lockReason}</p>
            </div>
          )
        )}
      </section>
    </div>
  );
}
